import { Link, useLocation } from '@tanstack/react-router';
import { Compass, LogOut, PanelLeft, Search, X } from 'lucide-react';
import { displayName, initials, useCurrentUser, usePredictionOperator } from '@/auth/useSession';
import { BrandMark } from '@/components/brand/BrandMark';
import { Tooltip } from '@/components/ui/tooltip';
import { useTour } from '@/features/tour/context';
import { cn } from '@/lib/cn';
import { isActivePath, screenOf, visibleNav } from './nav';

type SidebarProps = {
  /** Icon rail (60px): labels hidden, names in tooltips. */
  rail: boolean;
  /** Show the collapse/expand control (hidden when the rail is forced by width). */
  onToggleRail?: () => void;
  /** Mobile drawer variant: a close button instead of the collapse control. */
  onClose?: () => void;
  onOpenSearch: () => void;
  onLogout: () => void;
};

/** Matches the approved prototype's sidebar (Geist skin), section for section. */
export function Sidebar({ rail, onToggleRail, onClose, onOpenSearch, onLogout }: SidebarProps) {
  const { pathname } = useLocation();
  const me = useCurrentUser();
  const operator = usePredictionOperator();
  const groups = visibleNav({ role: me.data?.role, isPredictionOperator: operator.data === true });
  const name = displayName(me.data);
  const tour = useTour();

  return (
    <nav
      aria-label="Main"
      data-tour="nav"
      className={cn(
        'relative flex h-full flex-col bg-sidebar px-3 py-4',
        rail ? 'w-rail items-stretch' : 'w-side',
        'motion-safe:transition-[width] motion-safe:duration-[180ms]',
      )}
    >
      <div className={cn('flex items-center gap-2.5 pt-1 pb-[18px]', rail ? 'px-[9px]' : 'px-2.5')}>
        <BrandMark showName={!rail} />
        {onClose ? (
          <button
            type="button"
            onClick={onClose}
            aria-label="Close navigation"
            className="ml-auto grid size-[26px] place-items-center rounded-[5px] border-0 bg-transparent text-muted hover:bg-wash hover:text-fg"
          >
            <X className="size-4" aria-hidden="true" />
          </button>
        ) : onToggleRail ? (
          <button
            type="button"
            onClick={onToggleRail}
            aria-label={rail ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-expanded={!rail}
            className={cn(
              'grid size-[26px] place-items-center rounded-[5px] text-muted hover:bg-wash hover:text-fg',
              rail ? 'absolute top-3.5 left-11 z-10 border border-line bg-surface' : 'ml-auto border-0 bg-transparent',
            )}
          >
            <PanelLeft className="size-4" aria-hidden="true" />
          </button>
        ) : null}
      </div>

      <Tooltip content="Search" side="right" disabled={!rail}>
        <button
          type="button"
          onClick={onOpenSearch}
          aria-label="Search"
          aria-keyshortcuts="Meta+K Control+K"
          className={cn(
            'mb-4 flex h-9 w-full items-center gap-2 rounded-ctl border border-line bg-surface text-left text-muted hover:border-line-strong',
            rail ? 'justify-center px-0' : 'px-2.5',
          )}
        >
          <Search className="size-4 shrink-0" aria-hidden="true" />
          {rail ? null : (
            <>
              <span className="text-sm">Search</span>
              <kbd className="ml-auto rounded-[4px] border border-line bg-wash px-[5px] font-mono text-2xs text-muted">⌘K</kbd>
            </>
          )}
        </button>
      </Tooltip>

      <div className="-mx-3 min-h-0 flex-1 overflow-y-auto px-3">
        {groups.map((group) => (
          <div key={group.label ?? 'main'} {...(group.label ? { 'data-tour': `nav-${group.label.toLowerCase()}` } : {})}>
            {group.label && !rail ? (
              <div className="px-2.5 pt-5 pb-1.5 text-xs font-medium whitespace-nowrap text-muted">{group.label}</div>
            ) : group.label ? (
              <div className="mx-2.5 mt-4 mb-2 border-t border-line" role="presentation" />
            ) : null}
            <ul className="m-0 flex list-none flex-col gap-0.5 p-0">
              {group.items.map((item) => {
                const screen = screenOf(item);
                const active = isActivePath(pathname, screen.path);
                const Icon = item.icon;
                return (
                  <li key={item.screen}>
                    <Tooltip content={screen.title} side="right" disabled={!rail}>
                      <Link
                        to={screen.path}
                        aria-current={active ? 'page' : undefined}
                        aria-label={rail ? screen.title : undefined}
                        className={cn(
                          'flex h-[34px] items-center gap-3 rounded-ctl text-base whitespace-nowrap no-underline hover:bg-wash hover:text-fg',
                          rail ? 'justify-center px-0' : 'px-2.5',
                          active ? 'bg-wash font-medium text-fg' : 'text-muted',
                        )}
                      >
                        <Icon className="size-4 shrink-0" strokeWidth={1.5} aria-hidden="true" />
                        {rail ? null : <span>{screen.title}</span>}
                      </Link>
                    </Tooltip>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>

      <Tooltip content="Take the tour" side="right" disabled={!rail}>
        <button
          type="button"
          onClick={() => {
            onClose?.();
            tour.start('all');
          }}
          aria-label={rail ? 'Take the tour' : undefined}
          className={cn(
            'mt-3 mb-2 flex h-[34px] w-full items-center gap-3 rounded-ctl border-0 bg-transparent text-base whitespace-nowrap text-muted hover:bg-wash hover:text-fg',
            rail ? 'justify-center px-0' : 'px-2.5',
          )}
        >
          <Compass className="size-4 shrink-0" strokeWidth={1.5} aria-hidden="true" />
          {rail ? null : <span>Take the tour</span>}
        </button>
      </Tooltip>

      <div className={cn('mt-auto flex items-center gap-2.5 border-t border-line pt-3.5 pb-0.5', rail ? 'flex-col px-0' : 'px-2.5')}>
        <span aria-hidden="true" className="grid size-6 shrink-0 place-items-center rounded-full bg-wash text-2xs font-semibold">
          {initials(me.data)}
        </span>
        {rail ? null : (
          <span className="min-w-0 flex-1 truncate text-sm" title={me.data?.email_address}>
            {name}
          </span>
        )}
        <Tooltip content="Log out" side="right">
          <button
            type="button"
            onClick={onLogout}
            aria-label="Log out"
            className="grid size-7 shrink-0 place-items-center rounded-ctl border-0 bg-transparent text-muted hover:bg-wash hover:text-fg"
          >
            <LogOut className="size-4" aria-hidden="true" />
          </button>
        </Tooltip>
      </div>
    </nav>
  );
}
